import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Modal from '../components/common/Modal';
import ConfirmModal from '../components/common/ConfirmModal';
import { useAuth } from '../context/AuthContext';
import {
  getTemplates, createTemplate, updateTemplate, deleteTemplate, duplicateTemplate,
  getMergeFields, generateDocument, getGeneratedDocuments,
  getCases, getClients, getCustomLists, importDocxTemplate,
  uploadPdfTemplate, getPdfFields, savePdfMapping,
  getForms, deleteForm, duplicateForm
} from '../utils/api';
import { formatDate } from '../utils/helpers';
import {
  Plus, FileText, Search, Trash2, Pencil, Copy, Eye, X,
  Wand2, ArrowLeft, ChevronDown, Bold, Italic,
  Underline, AlignLeft, AlignCenter, AlignRight, Type, Image, Upload, Table2,
  ClipboardList, Globe, Lock, Link as LinkIcon, ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';

const DEFAULT_CATEGORIES = ['Correspondence','Letters','Fax Cover Sheets','Forms','Notices','Disengagements','Intake','Demand','Medical','Billing','Settlement','Miscellaneous'];

const FONT_FAMILIES = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: "'Times New Roman', serif", label: 'Times New Roman' },
  { value: "'Courier New', monospace", label: 'Courier New' },
  { value: "'Georgia', serif", label: 'Georgia' },
  { value: "'Verdana', sans-serif", label: 'Verdana' },
  { value: "'Trebuchet MS', sans-serif", label: 'Trebuchet MS' },
  { value: "'Calibri', sans-serif", label: 'Calibri' },
  { value: "'Cambria', serif", label: 'Cambria' },
  { value: "'Garamond', serif", label: 'Garamond' },
  { value: "'Tahoma', sans-serif", label: 'Tahoma' },
  { value: "'Palatino Linotype', serif", label: 'Palatino' },
  { value: "'Book Antiqua', serif", label: 'Book Antiqua' },
];

const FONT_SIZES = [
  { value: '1', label: '8' },
  { value: '2', label: '10' },
  { value: '3', label: '12' },
  { value: '4', label: '14' },
  { value: '5', label: '18' },
  { value: '6', label: '24' },
  { value: '7', label: '36' },
];

const FIELD_TYPES = [
  { value: 'all', label: 'All Fields' },
  { value: 'contact', label: 'Contact / Claimant' },
  { value: 'case', label: 'Case' },
  { value: 'insurance', label: 'Insurance / Adjuster' },
  { value: 'employer', label: 'Employer' },
  { value: 'benefits', label: 'Benefits' },
  { value: 'attorney', label: 'Attorney / Legal' },
  { value: 'firm', label: 'Firm' },
  { value: 'beneficiary', label: 'Beneficiary / Deceased' },
  { value: 'system', label: 'System / Dates' },
];

const ALL_MERGE_FIELDS = [
  { key: 'client_name', label: 'Contact (Full Name)', group: 'contact' },
  { key: 'client_first_name', label: 'First Name', group: 'contact' },
  { key: 'client_last_name', label: 'Last Name', group: 'contact' },
  { key: 'client_address', label: 'Home Address 1', group: 'contact' },
  { key: 'client_mailing_address', label: 'Mailing Address', group: 'contact' },
  { key: 'client_city', label: 'Home City', group: 'contact' },
  { key: 'client_state', label: 'Home State', group: 'contact' },
  { key: 'client_zip', label: 'Home ZIP Code', group: 'contact' },
  { key: 'client_county', label: 'County', group: 'contact' },
  { key: 'client_phone', label: 'Cell Phone', group: 'contact' },
  { key: 'client_phone_alt', label: 'Alt Phone', group: 'contact' },
  { key: 'client_email', label: "Client's E-mail", group: 'contact' },
  { key: 'client_ssn', label: 'SSN', group: 'contact' },
  { key: 'client_dob', label: 'DOB', group: 'contact' },
  { key: 'client_sex', label: 'Sex', group: 'contact' },
  { key: 'client_language', label: 'Language', group: 'contact' },
  { key: 'client_notes', label: 'Client Notes', group: 'contact' },
  { key: 'case_number', label: 'Case Number', group: 'case' },
  { key: 'claim_number', label: 'Claim Number', group: 'case' },
  { key: 'dwc_number', label: 'DWC', group: 'case' },
  { key: 'case_type', label: 'Case Type', group: 'case' },
  { key: 'case_status', label: 'Case Status', group: 'case' },
  { key: 'claim_status', label: 'Current Case Status', group: 'case' },
  { key: 'claim_type', label: 'Claim Type', group: 'case' },
  { key: 'date_of_incident', label: 'DOI', group: 'case' },
  { key: 'body_parts', label: 'Body Parts', group: 'case' },
  { key: 'mechanism_of_injury', label: 'Mechanism', group: 'case' },
  { key: 'compensable', label: 'Compensable Inj', group: 'case' },
  { key: 'disputes', label: 'Disputed Issues', group: 'case' },
  { key: 'demand_amount', label: 'Demand Amount', group: 'case' },
  { key: 'settlement_amount', label: 'Settlement Amount', group: 'case' },
  { key: 'referred_by', label: 'Referred By', group: 'case' },
  { key: 'insurance_company', label: 'Carrier Name', group: 'insurance' },
  { key: 'insurance_network', label: 'Provider Network', group: 'insurance' },
  { key: 'insurance_address', label: 'Carrier Address', group: 'insurance' },
  { key: 'insurance_city_state_zip', label: 'Carrier City/St/Zip', group: 'insurance' },
  { key: 'insurance_phone', label: 'Carrier Phone', group: 'insurance' },
  { key: 'insurance_fax', label: 'Carrier Fax', group: 'insurance' },
  { key: 'adjuster_name', label: 'Adjuster Name', group: 'insurance' },
  { key: 'adjuster_email', label: 'Adjuster Email', group: 'insurance' },
  { key: 'adjuster_phone', label: 'Adjuster Phone', group: 'insurance' },
  { key: 'adjuster_fax', label: 'Adjuster Fax', group: 'insurance' },
  { key: 'insurance_claim', label: 'Ins Claim#', group: 'insurance' },
  { key: 'insurance_policy', label: 'Ins Policy#', group: 'insurance' },
  { key: 'employer_company', label: 'Employer', group: 'employer' },
  { key: 'employer_address', label: 'Employer Address', group: 'employer' },
  { key: 'employer_city', label: 'Employer City', group: 'employer' },
  { key: 'employer_state', label: 'Employer State', group: 'employer' },
  { key: 'employer_zip', label: 'Employer Zip', group: 'employer' },
  { key: 'employer_phone', label: 'Employer Phone', group: 'employer' },
  { key: 'aww', label: 'AWW', group: 'benefits' },
  { key: 'benefit_rate', label: '70% of AWW', group: 'benefits' },
  { key: 'actual_benefit', label: 'Actual Amt', group: 'benefits' },
  { key: 'benefit_type', label: 'Benefit Type', group: 'benefits' },
  { key: 'work_status', label: 'Work Status', group: 'benefits' },
  { key: 'attorney_fees', label: 'Attorney Fees', group: 'benefits' },
  { key: 'attorney_name', label: 'Attorney Name', group: 'attorney' },
  { key: 'attorney_email', label: 'Attorney Email', group: 'attorney' },
  { key: 'attorney_phone', label: 'Attorney Phone', group: 'attorney' },
  { key: 'firm_name', label: 'Firm Name', group: 'firm' },
  { key: 'firm_address', label: 'Firm Address', group: 'firm' },
  { key: 'firm_phone', label: 'Firm Phone', group: 'firm' },
  { key: 'firm_email', label: 'Firm Email', group: 'firm' },
  { key: 'firm_logo', label: 'Firm Logo', group: 'firm' },
  { key: 'beneficiary_name', label: 'Benif_Name', group: 'beneficiary' },
  { key: 'beneficiary_address', label: 'Benif_Address', group: 'beneficiary' },
  { key: 'beneficiary_phone', label: 'Benif_Phone', group: 'beneficiary' },
  { key: 'beneficiary_email', label: 'Benif_Email', group: 'beneficiary' },
  { key: 'beneficiary_ss', label: 'Benif_SS', group: 'beneficiary' },
  { key: 'beneficiary_relationship', label: 'Benif_Type', group: 'beneficiary' },
  { key: 'beneficiary_notes', label: 'Benif_Notes', group: 'beneficiary' },
  { key: 'today_date', label: 'Date (Short)', group: 'system' },
  { key: 'today_date_long', label: 'Date (Long)', group: 'system' },
  { key: 'create_date', label: 'Create Date', group: 'system' },
];

export default function DocumentTemplatesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const logoUrl = user?.organization?.logo_url || '';

  const [templates, setTemplates] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  const [mode, setMode] = useState('list');
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category: '', body: '', header_html: '', footer_html: '', margins: { top: 1, bottom: 0.5, left: 1, right: 1 } });
  const [saving, setSaving] = useState(false);

  const [fieldTypeFilter, setFieldTypeFilter] = useState('all');
  const [fieldSearch, setFieldSearch] = useState('');

  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const footerRef = useRef(null);
  const [activeEditor, setActiveEditor] = useState('body');
  const [showHeaderSection, setShowHeaderSection] = useState(false);
  const [showFooterSection, setShowFooterSection] = useState(false);

  const [showGenerate, setShowGenerate] = useState(null);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [genForm, setGenForm] = useState({ case_id: '', client_id: '' });
  const [generating, setGenerating] = useState(false);
  const [generatedDocs, setGeneratedDocs] = useState([]);
  const [showGenerated, setShowGenerated] = useState(false);

  // .docx import — direct file picker, no modal
  const [importing, setImporting] = useState(false);
  const docxInputRef = useRef(null);

  // PDF template upload
  const [pdfUploading, setPdfUploading] = useState(false);
  const pdfInputRef = useRef(null);
  const [pdfFields, setPdfFields] = useState([]);        // detected fields from PDF
  const [pdfMapping, setPdfMapping] = useState([]);        // current mapping
  const [pdfMappingSaving, setPdfMappingSaving] = useState(false);
  const [showPdfMapping, setShowPdfMapping] = useState(false);

  const handlePdfFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) { toast.error('Please select a PDF file'); return; }
    setPdfUploading(true);
    try {
      const result = await uploadPdfTemplate(file, file.name.replace(/\.pdf$/i, ''), catFilter || 'forms');
      toast.success('PDF template uploaded');
      if (result.template) {
        setTemplates(prev => [{ ...result.template, _id: result.template.id || result.template._id }, ...prev]);
        // Open field mapping immediately
        setSelected(result.template);
        setPdfFields(result.pdf_fields || []);
        setPdfMapping((result.pdf_fields || []).map(f => ({ pdf_field: f.name, merge_key: '' })));
        setShowPdfMapping(true);
      }
    } catch (err) { toast.error(err.message || 'PDF upload failed'); }
    finally { setPdfUploading(false); }
  };

  const loadPdfFieldMapping = async (templateId) => {
    try {
      const result = await getPdfFields(templateId);
      setPdfFields(result.fields || []);
      const existing = result.mapping || [];
      // Merge: keep existing mappings, add any new fields
      const mapped = (result.fields || []).map(f => {
        const ex = existing.find(m => m.pdf_field === f.name);
        return { pdf_field: f.name, merge_key: ex?.merge_key || '' };
      });
      setPdfMapping(mapped);
    } catch (err) { toast.error('Failed to load PDF fields'); }
  };

  const handleSavePdfMapping = async () => {
    if (!selected?._id) return;
    setPdfMappingSaving(true);
    try {
      await savePdfMapping(selected._id, pdfMapping.filter(m => m.merge_key));
      toast.success('Field mapping saved');
      setShowPdfMapping(false);
    } catch (err) { toast.error(err.message || 'Failed to save mapping'); }
    finally { setPdfMappingSaving(false); }
  };

  const triggerDocxImport = () => {
    if (docxInputRef.current) {
      docxInputRef.current.value = '';
      docxInputRef.current.click();
    }
  };

  const handleDocxFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.docx?$/i.test(file.name)) return toast.error('Please select a .docx file');

    setImporting(true);
    try {
      const result = await importDocxTemplate(file, categories[0] || 'Correspondence');
      const t = result.template;
      if (result.warnings?.length) console.warn('Docx import warnings:', result.warnings);

      // Open the newly created template in the editor
      setEditing(t);
      setForm({
        name: t.name || '', description: t.description || '', category: t.category || categories[0] || '',
        body: t.body || '', header_html: t.header_html || '', footer_html: t.footer_html || '',
        margins: t.margins || { top: 1, bottom: 0.5, left: 1, right: 1 },
      });
      setFieldTypeFilter('all');
      setFieldSearch('');
      setShowHeaderSection(!!(t.header_html && t.header_html.trim()));
      setShowFooterSection(!!(t.footer_html && t.footer_html.trim()));
      setActiveEditor('body');
      setMode('editor');
      setTimeout(() => {
        if (headerRef.current) headerRef.current.innerHTML = t.header_html || '';
        if (bodyRef.current) bodyRef.current.innerHTML = t.body || '';
        if (footerRef.current) footerRef.current.innerHTML = t.footer_html || '';
      }, 80);

      load(); // refresh list in background
      toast.success('Document imported — review and add merge fields, then save');
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err?.message || 'Failed to import document');
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => { load(); loadCustomLists(); }, []);
  useEffect(() => { load(); }, [catFilter]);

  const loadCustomLists = async () => {
    try {
      const lists = await getCustomLists();
      if (lists?.template_category_options?.length) setCategories(lists.template_category_options);
    } catch {}
  };

  const load = async () => {
    try {
      setLoading(true);
      const [data, formData] = await Promise.all([
        getTemplates(catFilter ? { category: catFilter } : undefined),
        getForms().catch(() => []),
      ]);
      setTemplates(Array.isArray(data) ? data : []);
      setForms(Array.isArray(formData) ? formData : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t => t.name.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q));
  }, [templates, searchQ]);

  const filteredForms = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return forms;
    return forms.filter(f => f.name.toLowerCase().includes(q) || (f.description || '').toLowerCase().includes(q));
  }, [forms, searchQ]);

  const filteredFields = useMemo(() => {
    let fields = ALL_MERGE_FIELDS;
    if (fieldTypeFilter !== 'all') fields = fields.filter(f => f.group === fieldTypeFilter);
    const q = fieldSearch.trim().toLowerCase();
    if (q) fields = fields.filter(f => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q));
    return fields;
  }, [fieldTypeFilter, fieldSearch]);

  const getActiveRef = () => {
    if (activeEditor === 'header' && showHeaderSection) return headerRef.current;
    if (activeEditor === 'footer' && showFooterSection) return footerRef.current;
    return bodyRef.current;
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', description: '', category: categories[0] || '', body: '', header_html: '', footer_html: '', margins: { top: 1, bottom: 0.5, left: 1, right: 1 } });
    setFieldTypeFilter('all');
    setFieldSearch('');
    setShowHeaderSection(false);
    setShowFooterSection(false);
    setActiveEditor('body');
    setMode('editor');
    setTimeout(() => {
      if (headerRef.current) headerRef.current.innerHTML = '';
      if (bodyRef.current) bodyRef.current.innerHTML = '';
      if (footerRef.current) footerRef.current.innerHTML = '';
    }, 50);
  };

  const openEdit = (t) => {
    // PDF templates open field mapping instead of HTML editor
    if (t.template_type === 'pdf') {
      setSelected(t);
      loadPdfFieldMapping(t._id || t.id);
      setShowPdfMapping(true);
      return;
    }
    setEditing(t);
    setForm({
      name: t.name || '', description: t.description || '', category: t.category || categories[0] || '',
      body: t.body || '', header_html: t.header_html || '', footer_html: t.footer_html || '',
      margins: t.margins || { top: 1, bottom: 0.5, left: 1, right: 1 },
    });
    setFieldTypeFilter('all');
    setFieldSearch('');
    setShowHeaderSection(!!(t.header_html && t.header_html.trim()));
    setShowFooterSection(!!(t.footer_html && t.footer_html.trim()));
    setActiveEditor('body');
    setMode('editor');
    setTimeout(() => {
      if (headerRef.current) headerRef.current.innerHTML = t.header_html || '';
      if (bodyRef.current) bodyRef.current.innerHTML = t.body || '';
      if (footerRef.current) footerRef.current.innerHTML = t.footer_html || '';
    }, 50);
  };

  const syncFormFromEditors = () => ({
    ...form,
    header_html: showHeaderSection ? (headerRef.current?.innerHTML || '') : '',
    body: bodyRef.current?.innerHTML || '',
    footer_html: showFooterSection ? (footerRef.current?.innerHTML || '') : '',
    margins: form.margins,
  });

  const handleSave = async () => {
    const data = syncFormFromEditors();
    if (!data.name.trim()) return toast.error('Template name required');
    const bodyText = (bodyRef.current?.innerText || '').trim();
    if (!bodyText) return toast.error('Template body required');
    setSaving(true);
    try {
      if (editing) { await updateTemplate(editing._id, data); toast.success('Template updated'); }
      else { await createTemplate(data); toast.success('Template created'); }
      setMode('list');
      load();
    } catch { toast.error('Failed to save template'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (t) => {
    setConfirmAction({
      title: 'Delete Template',
      message: `Delete "${t.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => { try { await deleteTemplate(t._id); toast.success('Template deleted'); load(); } catch { toast.error('Failed to delete'); } },
    });
  };

  const handleDuplicate = async (t) => {
    try {
      const copy = await duplicateTemplate(t._id);
      toast.success(`Duplicated: ${copy.name}`);
      load();
    } catch { toast.error('Failed to duplicate template'); }
  };

  // Table insert
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [tableBorders, setTableBorders] = useState({ top: true, bottom: true, left: true, right: true, inner: true });
  const [confirmAction, setConfirmAction] = useState(null);
  const tableMenuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (tableMenuRef.current && !tableMenuRef.current.contains(e.target)) setShowTableMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allBordersOn = tableBorders.top && tableBorders.bottom && tableBorders.left && tableBorders.right && tableBorders.inner;
  const noBordersOn = !tableBorders.top && !tableBorders.bottom && !tableBorders.left && !tableBorders.right && !tableBorders.inner;

  const insertTable = () => {
    const ref = getActiveRef();
    if (!ref) return;

    const b = tableBorders;
    const solid = '1px solid #000';
    const none = '1px solid transparent';
    const rows = [];
    for (let r = 0; r < tableRows; r++) {
      const cells = [];
      for (let c = 0; c < tableCols; c++) {
        const isLastCol = c === tableCols - 1;
        const isLastRow = r === tableRows - 1;
        const styles = [
          `border-top:${b.top && r === 0 ? solid : (b.inner && r > 0 ? solid : none)}`,
          `border-bottom:${b.bottom && isLastRow ? solid : (b.inner && !isLastRow ? solid : none)}`,
          `border-left:${b.left && c === 0 ? solid : (b.inner && c > 0 ? solid : none)}`,
          `border-right:${b.right && isLastCol ? solid : (b.inner && !isLastCol ? solid : none)}`,
          'padding:6px 8px',
          'font-size:11pt',
          'vertical-align:top',
        ].join(';');
        cells.push(`<td style="${styles}">&nbsp;</td>`);
      }
      rows.push(`<tr>${cells.join('')}</tr>`);
    }
    const tableHTML = `<table style="border-collapse:collapse;width:100%;margin:8px 0">${rows.join('')}</table><p><br></p>`;

    // Try to insert at saved cursor position first
    let inserted = false;
    if (savedSelectionRef.current) {
      try {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedSelectionRef.current);
        // Verify selection is inside this editor
        if (ref.contains(sel.anchorNode)) {
          document.execCommand('insertHTML', false, tableHTML);
          inserted = true;
        }
      } catch {}
    }

    // Fallback: directly append to the editor element
    if (!inserted) {
      ref.insertAdjacentHTML('beforeend', tableHTML);
    }

    setShowTableMenu(false);
    toast.success('Table inserted');
  };

  const execCmd = (cmd, value = null) => {
    const ref = getActiveRef();
    if (ref) ref.focus();
    document.execCommand(cmd, false, value);
  };

  // Save selection whenever a contentEditable section has focus
  const savedSelectionRef = useRef(null);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      // Only save if it's inside one of our editors
      const node = range.startContainer;
      if (headerRef.current?.contains(node) || bodyRef.current?.contains(node) || footerRef.current?.contains(node)) {
        savedSelectionRef.current = range.cloneRange();
      }
    }
  };

  const restoreSelection = () => {
    const range = savedSelectionRef.current;
    if (range) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      return true;
    }
    return false;
  };

  const insertField = useCallback((key) => {
    // Restore selection first — this is the cursor position before clicking the merge field button
    if (!restoreSelection()) {
      // Fallback: focus the active editor and place cursor at end
      const ref = getActiveRef();
      if (!ref) return;
      ref.focus();
      const sel = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(ref);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    const html = `<span style="background:#fef3c7;color:#92400e;padding:1px 4px;border-radius:3px;font-size:0.85em;border:1px solid #fde68a;font-weight:600;display:inline" contenteditable="false" data-merge="${key}">&lt;${key}&gt;</span>\u00A0`;
    document.execCommand('insertHTML', false, html);
  }, [activeEditor]);

  const insertLogo = () => {
    if (!logoUrl) return toast.error('No firm logo uploaded. Go to Settings → Firm Settings to upload.');
    if (!restoreSelection()) {
      const ref = getActiveRef();
      if (!ref) return;
      ref.focus();
    }
    const baseUrl = window.location.origin;
    const html = `<img src="${baseUrl}${logoUrl}" alt="Firm Logo" style="max-height:80px;max-width:250px;object-fit:contain;" />`;
    document.execCommand('insertHTML', false, html);
    toast.success('Logo inserted');
  };

  // Generate
  const openGenerate = async (template) => {
    setShowGenerate(template);
    setGenForm({ case_id: '', client_id: '' });
    try {
      const [c, cl] = await Promise.all([getCases({ limit: 100 }), getClients({ limit: 100 })]);
      setCases(Array.isArray(c) ? c : (c?.cases || []));
      setClients(cl?.clients || []);
    } catch {}
  };

  const handleGenerate = async () => {
    if (!genForm.case_id && !genForm.client_id) return toast.error('Select a case or client');
    setGenerating(true);
    try {
      const doc = await generateDocument(showGenerate._id, genForm);
      toast.success('Document generated!');
      setShowGenerate(null);

      // Fetch the preview HTML (with auth header) then write to a new tab
      const token = localStorage.getItem('zeus_token');
      const resp = await fetch(`/api/templates/documents/generated/${doc._id}?format=preview`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const html = await resp.text();
      const win = window.open('', '_blank');
      if (win) {
        win.document.open();
        win.document.write(html);
        win.document.close();
      }
    } catch { toast.error('Failed to generate document'); }
    finally { setGenerating(false); }
  };

  const loadGeneratedDocs = async () => {
    try { const docs = await getGeneratedDocuments(); setGeneratedDocs(Array.isArray(docs) ? docs : []); setShowGenerated(true); }
    catch { toast.error('Failed to load documents'); }
  };

  const editableStyle = { outline: 'none', cursor: 'text', whiteSpace: 'pre-wrap', wordWrap: 'break-word' };

  const sectionLabelStyle = {
    fontSize: '0.65rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em',
    fontWeight: 700, padding: '2px 6px', background: '#f0f0f0', borderRadius: '3px',
    display: 'inline-block', marginBottom: '4px', userSelect: 'none',
  };

  const TBtn = ({ title, onClick, children }) => (
    <button
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      style={{
        padding: '5px 7px', background: 'transparent', border: '1px solid var(--zeus-border)',
        borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--text-secondary)',
        display: 'flex', alignItems: 'center',
      }}
    >{children}</button>
  );

  // ═══════════════════════════════════════════
  // EDITOR VIEW — WYSIWYG Rich Text
  // ═══════════════════════════════════════════
  if (mode === 'editor') {
    return (
      <>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 20px', borderBottom: '1px solid var(--zeus-border)', background: 'var(--zeus-navy)' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setMode('list')}><ArrowLeft size={16} /> Back</button>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input className="form-input" style={{ maxWidth: '280px', padding: '6px 10px', fontSize: '0.9rem', fontWeight: 600 }} placeholder="Template Name..." value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} />
            <select className="form-select" style={{ width: 'auto', padding: '6px 10px', fontSize: '0.82rem' }} value={form.category} onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}>
              <option value="">— Category —</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editing ? 'Update Template' : 'Save Template'}
          </button>
        </div>

        {/* Main editor layout */}
        <div style={{ display: 'flex', height: 'calc(100vh - 52px)', overflow: 'hidden' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* ── Formatting Toolbar ── */}
            <div style={{ display: 'flex', gap: '4px', padding: '8px 16px', borderBottom: '1px solid var(--zeus-border)', background: 'var(--zeus-navy-light)', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Font Family */}
              <select className="form-select" style={{ width: '140px', padding: '4px 6px', fontSize: '0.78rem' }} defaultValue="" onChange={(e) => { if (e.target.value) { execCmd('fontName', e.target.value); e.target.value = ''; } }}>
                <option value="" disabled>Font</option>
                {FONT_FAMILIES.map(f => <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>)}
              </select>
              {/* Font Size */}
              <select className="form-select" style={{ width: '68px', padding: '4px 6px', fontSize: '0.78rem' }} defaultValue="" onChange={(e) => { if (e.target.value) { execCmd('fontSize', e.target.value); e.target.value = ''; } }}>
                <option value="" disabled>Size</option>
                {FONT_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}pt</option>)}
              </select>

              <div style={{ width: '1px', height: '22px', background: 'var(--zeus-border)', margin: '0 4px' }} />

              <TBtn title="Bold (Ctrl+B)" onClick={() => execCmd('bold')}><Bold size={15} /></TBtn>
              <TBtn title="Italic (Ctrl+I)" onClick={() => execCmd('italic')}><Italic size={15} /></TBtn>
              <TBtn title="Underline (Ctrl+U)" onClick={() => execCmd('underline')}><Underline size={15} /></TBtn>

              <div style={{ width: '1px', height: '22px', background: 'var(--zeus-border)', margin: '0 4px' }} />

              <TBtn title="Align Left" onClick={() => execCmd('justifyLeft')}><AlignLeft size={15} /></TBtn>
              <TBtn title="Align Center" onClick={() => execCmd('justifyCenter')}><AlignCenter size={15} /></TBtn>
              <TBtn title="Align Right" onClick={() => execCmd('justifyRight')}><AlignRight size={15} /></TBtn>

              <div style={{ width: '1px', height: '22px', background: 'var(--zeus-border)', margin: '0 4px' }} />

              <TBtn title="Insert Firm Logo" onClick={insertLogo}><Image size={15} /></TBtn>

              {/* Table insert */}
              <div ref={tableMenuRef} style={{ position:'relative' }}>
                <TBtn title="Insert Table" onClick={() => setShowTableMenu(!showTableMenu)}><Table2 size={15} /></TBtn>
                {showTableMenu && (
                  <div style={{
                    position:'absolute', top:'100%', left:0, zIndex:50,
                    background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
                    borderRadius:'var(--radius-md)', padding:'14px', width:'240px',
                    boxShadow:'0 4px 20px rgba(0,0,0,0.4)',
                  }}>
                    <div style={{ fontSize:'0.82rem', fontWeight:700, marginBottom:'10px', color:'var(--text-primary)' }}>Insert Table</div>

                    {/* Size */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'12px' }}>
                      <div>
                        <label style={{ fontSize:'0.7rem', color:'var(--text-muted)', display:'block', marginBottom:'3px' }}>Rows</label>
                        <input type="number" className="form-input" min="1" max="20" value={tableRows}
                          onChange={(e) => setTableRows(parseInt(e.target.value) || 1)}
                          style={{ padding:'4px 8px', fontSize:'0.82rem', width:'100%' }} />
                      </div>
                      <div>
                        <label style={{ fontSize:'0.7rem', color:'var(--text-muted)', display:'block', marginBottom:'3px' }}>Columns</label>
                        <input type="number" className="form-input" min="1" max="10" value={tableCols}
                          onChange={(e) => setTableCols(parseInt(e.target.value) || 1)}
                          style={{ padding:'4px 8px', fontSize:'0.82rem', width:'100%' }} />
                      </div>
                    </div>

                    {/* Borders */}
                    <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--text-muted)', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.04em' }}>Borders</div>

                    {/* Quick toggles */}
                    <div style={{ display:'flex', gap:'6px', marginBottom:'8px' }}>
                      <button
                        className={`btn btn-sm ${allBordersOn ? 'btn-primary' : 'btn-ghost'}`}
                        style={{ flex:1, fontSize:'0.72rem', padding:'4px 0' }}
                        onMouseDown={(e) => { e.preventDefault(); setTableBorders({ top:true, bottom:true, left:true, right:true, inner:true }); }}
                      >All Borders</button>
                      <button
                        className={`btn btn-sm ${noBordersOn ? 'btn-primary' : 'btn-ghost'}`}
                        style={{ flex:1, fontSize:'0.72rem', padding:'4px 0' }}
                        onMouseDown={(e) => { e.preventDefault(); setTableBorders({ top:false, bottom:false, left:false, right:false, inner:false }); }}
                      >No Borders</button>
                    </div>

                    {/* Individual borders */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'3px', marginBottom:'12px' }}>
                      {[
                        { k:'top', l:'Top' },
                        { k:'bottom', l:'Bottom' },
                        { k:'left', l:'Left' },
                        { k:'right', l:'Right' },
                        { k:'inner', l:'Inner Lines' },
                      ].map(({k,l}) => (
                        <label key={k} style={{
                          display:'flex', alignItems:'center', gap:'6px', fontSize:'0.75rem',
                          color: tableBorders[k] ? 'var(--text-primary)' : 'var(--text-muted)',
                          cursor:'pointer', padding:'3px 4px', borderRadius:'4px',
                          background: tableBorders[k] ? 'rgba(212,168,83,0.08)' : 'transparent',
                          gridColumn: k === 'inner' ? '1 / -1' : undefined,
                        }}>
                          <input type="checkbox" checked={tableBorders[k]}
                            onChange={(e) => setTableBorders(prev => ({...prev, [k]: e.target.checked}))}
                            style={{ accentColor:'var(--zeus-gold)', width:'14px', height:'14px' }} />
                          {l}
                        </label>
                      ))}
                    </div>

                    {/* Preview hint */}
                    <div style={{ fontSize:'0.68rem', color:'var(--text-muted)', marginBottom:'10px', textAlign:'center', fontStyle:'italic' }}>
                      {noBordersOn ? 'Invisible grid — data will align without lines' :
                       allBordersOn ? 'Full grid with all border lines' :
                       'Custom border configuration'}
                    </div>

                    <button className="btn btn-primary btn-sm" style={{ width:'100%', padding:'6px 0' }}
                      onMouseDown={(e) => { e.preventDefault(); insertTable(); }}>
                      Insert {tableRows}×{tableCols} Table
                    </button>
                  </div>
                )}
              </div>

              <div style={{ width: '1px', height: '22px', background: 'var(--zeus-border)', margin: '0 4px' }} />

              {/* Header / Footer toggles */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  setShowHeaderSection(v => {
                    if (!v) setTimeout(() => { if (headerRef.current) { headerRef.current.focus(); setActiveEditor('header'); } }, 60);
                    return !v;
                  });
                }}
                title={showHeaderSection ? 'Remove Header' : 'Add Header'}
                style={{
                  padding: '4px 8px', fontSize: '0.72rem', fontWeight: 600,
                  background: showHeaderSection ? 'rgba(212,168,83,0.15)' : 'transparent',
                  border: `1px solid ${showHeaderSection ? 'var(--zeus-gold)' : 'var(--zeus-border)'}`,
                  borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  color: showHeaderSection ? 'var(--zeus-gold)' : 'var(--text-muted)',
                }}
              >{showHeaderSection ? ' Header' : '+ Header'}</button>

              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  setShowFooterSection(v => {
                    if (!v) setTimeout(() => { if (footerRef.current) { footerRef.current.focus(); setActiveEditor('footer'); } }, 60);
                    return !v;
                  });
                }}
                title={showFooterSection ? 'Remove Footer' : 'Add Footer'}
                style={{
                  padding: '4px 8px', fontSize: '0.72rem', fontWeight: 600,
                  background: showFooterSection ? 'rgba(212,168,83,0.15)' : 'transparent',
                  border: `1px solid ${showFooterSection ? 'var(--zeus-gold)' : 'var(--zeus-border)'}`,
                  borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  color: showFooterSection ? 'var(--zeus-gold)' : 'var(--text-muted)',
                }}
              >{showFooterSection ? ' Footer' : '+ Footer'}</button>

              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: '10px' }}>
                Merge fields →
              </span>
            </div>

            {/* ── Document Page ── */}
            <div style={{ flex: 1, padding: '20px', overflow: 'auto', background: 'var(--zeus-black)' }}>
              <div style={{
                maxWidth: '8.5in', width: '8.5in', minHeight: '11in', margin: '0 auto', background: '#fff', color: '#000',
                padding: `${form.margins.top}in ${form.margins.right}in ${form.margins.bottom}in ${form.margins.left}in`,
                boxShadow: '0 2px 20px rgba(0,0,0,0.3)', borderRadius: '4px',
                fontFamily: "'Times New Roman', serif", fontSize: '11pt', lineHeight: 1.6,
              }}>
                {/* HEADER — only if enabled */}
                {showHeaderSection && (
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'2px' }}>
                      <div style={sectionLabelStyle}>HEADER</div>
                      <button onClick={() => { setShowHeaderSection(false); if (activeEditor === 'header') setActiveEditor('body'); }}
                        style={{ fontSize:'0.62rem', color:'#999', background:'none', border:'none', cursor:'pointer', padding:'1px 4px' }}
                        title="Remove header"></button>
                    </div>
                    <div ref={headerRef} contentEditable suppressContentEditableWarning
                      onFocus={() => setActiveEditor('header')}
                      onBlur={saveSelection}
                      onKeyUp={saveSelection}
                      onMouseUp={saveSelection}
                      style={{ ...editableStyle, minHeight: '36px', padding: '6px 4px', borderBottom: '1px dashed #d1d5db', marginBottom: '8px' }}
                      data-placeholder="Click here to add header — use toolbar for font, size, logo"
                    />
                  </div>
                )}

                {/* BODY — always visible */}
                <div>
                  {(showHeaderSection || showFooterSection) && <div style={sectionLabelStyle}>BODY</div>}
                  <div ref={bodyRef} contentEditable suppressContentEditableWarning
                    onFocus={() => setActiveEditor('body')}
                    onBlur={saveSelection}
                    onKeyUp={saveSelection}
                    onMouseUp={saveSelection}
                    style={{ ...editableStyle, minHeight: showHeaderSection || showFooterSection ? '7.5in' : '9.5in', padding: '6px 4px' }}
                    data-placeholder="Start typing your template... Select text to change Font or Size. Click fields from the panel to insert merge fields."
                  />
                </div>

                {/* FOOTER — only if enabled */}
                {showFooterSection && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'2px' }}>
                      <div style={sectionLabelStyle}>FOOTER</div>
                      <button onClick={() => { setShowFooterSection(false); if (activeEditor === 'footer') setActiveEditor('body'); }}
                        style={{ fontSize:'0.62rem', color:'#999', background:'none', border:'none', cursor:'pointer', padding:'1px 4px' }}
                        title="Remove footer"></button>
                    </div>
                    <div ref={footerRef} contentEditable suppressContentEditableWarning
                      onFocus={() => setActiveEditor('footer')}
                      onBlur={saveSelection}
                      onKeyUp={saveSelection}
                      onMouseUp={saveSelection}
                      style={{ ...editableStyle, minHeight: '36px', padding: '6px 4px', borderTop: '1px dashed #d1d5db', marginTop: '4px' }}
                      data-placeholder="Click here to add footer — firm address, phone, logo, etc."
                    />
                  </div>
                )}
              </div>
            </div>

            <style>{`
              [contenteditable][data-placeholder]:empty::before { content: attr(data-placeholder); color: #aaa; font-style: italic; pointer-events: none; }
              [contenteditable]:focus { outline: 2px solid rgba(212,168,83,0.3); outline-offset: 2px; border-radius: 2px; }
            `}</style>
          </div>

          {/* ── RIGHT: Merge Field Panel ── */}
          <div style={{ width: '280px', borderLeft: '1px solid var(--zeus-border)', background: 'var(--zeus-navy)', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>

            {/* Margin Controls */}
            <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--zeus-border)', background: 'var(--zeus-navy-light)' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: '6px', color: 'var(--text-primary)' }}>Page Margins (inches)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                {[{k:'top',l:'Top'},{k:'bottom',l:'Bottom'},{k:'left',l:'Left'},{k:'right',l:'Right'}].map(({ k, l }) => (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <label style={{ fontSize: '0.68rem', color: 'var(--text-muted)', width: '38px', flexShrink: 0 }}>{l}</label>
                    <input className="form-input" type="number" step="0.1" min="0" max="3"
                      style={{ padding: '3px 6px', fontSize: '0.78rem', width: '60px', textAlign: 'center' }}
                      value={form.margins[k]}
                      onChange={(e) => setForm(prev => ({ ...prev, margins: { ...prev.margins, [k]: parseFloat(e.target.value) || 0 } }))}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div style={{ padding: '12px', borderBottom: '1px solid var(--zeus-border)', background: 'var(--zeus-navy-light)' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: '8px', color: 'var(--text-primary)' }}>Add Mail Merge Fields</div>
              <label style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Select Field Type</label>
              <select className="form-select" style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem', marginBottom: '8px' }} value={fieldTypeFilter} onChange={(e) => setFieldTypeFilter(e.target.value)}>
                {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
              </select>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 8px', border: '1px solid var(--zeus-border)', borderRadius: 'var(--radius-sm)', background: 'var(--zeus-navy-light)' }}>
                <Search size={13} style={{ color: 'var(--text-muted)' }} />
                <input className="form-input" style={{ border: 'none', outline: 'none', background: 'transparent', padding: 0, fontSize: '0.8rem', flex: 1 }} placeholder="Search fields..." value={fieldSearch} onChange={(e) => setFieldSearch(e.target.value)} />
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
              {filteredFields.map(f => (
                <div key={f.key}
                  onMouseDown={(e) => { e.preventDefault(); insertField(f.key); }}
                  style={{ padding: '6px 12px', fontSize: '0.82rem', color: 'var(--text-primary)', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.03)', transition: 'background 0.15s' }}
                  onMouseOver={(e) => e.currentTarget.style.background = 'var(--zeus-gold-dim)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  {f.label}
                  <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginLeft: '6px' }}>&lt;{f.key}&gt;</span>
                </div>
              ))}
              {filteredFields.length === 0 && (
                <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>No fields found</div>
              )}
            </div>

            <div style={{ padding: '10px 12px', borderTop: '1px solid var(--zeus-border)' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center' }}>Click a field to insert at cursor</div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '4px' }}>
                Editing: <span style={{ color: 'var(--zeus-gold)', fontWeight: 600, textTransform: 'uppercase' }}>{activeEditor}</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ═══════════════════════════════════════════
  // LIST VIEW
  // ═══════════════════════════════════════════
  return (
    <>
      <Header title="Document & Form Templates">
        <button className="btn btn-ghost btn-sm" onClick={loadGeneratedDocs}><FileText size={14} /> Generated Docs</button>
        <button className="btn btn-secondary btn-sm" onClick={triggerDocxImport} disabled={importing} style={{ display:'flex', alignItems:'center', gap:'5px' }}>
          {importing ? <><div className="spinner" style={{ width:'14px', height:'14px', borderWidth:'2px' }} /> Importing...</> : <><Upload size={14} /> Import .docx</>}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => pdfInputRef.current?.click()} disabled={pdfUploading} style={{ display:'flex', alignItems:'center', gap:'5px' }}>
          {pdfUploading ? <><div className="spinner" style={{ width:'14px', height:'14px', borderWidth:'2px' }} /> Uploading...</> : <><Upload size={14} /> Upload PDF Form</>}
        </button>
        <button className="btn btn-primary btn-sm" onClick={openNew}><Plus size={14} /> New Template</button>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/form-builder/new')} style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', borderColor: '#3b82f6' }}><ClipboardList size={14} /> New Form</button>
      </Header>

      {/* Hidden file input for .docx import */}
      <input ref={docxInputRef} type="file" accept=".docx,.doc" style={{ display:'none' }} onChange={handleDocxFileSelected} />
      <input ref={pdfInputRef} type="file" accept=".pdf,application/pdf" style={{ display:'none' }} onChange={handlePdfFileSelected} />

      <div className="app-content">
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', border: '1px solid var(--zeus-border)', borderRadius: '12px', background: 'var(--zeus-navy-light)' }}>
            <Search size={16} style={{ color: 'var(--text-muted)' }} />
            <input className="form-input" style={{ border: 'none', outline: 'none', background: 'transparent', padding: 0, minWidth: '200px' }} placeholder="Search templates..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
          </div>
          <select className="form-select" style={{ width: 'auto', padding: '8px 12px', fontSize: '0.85rem' }} value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <FileText /><h3>No templates</h3>
            <p>Create a template from scratch or import from a Word document.</p>
            <div style={{ display:'flex', gap:'8px', justifyContent:'center' }}>
              <button className="btn btn-secondary" onClick={triggerDocxImport} disabled={importing}><Upload size={14} /> Import .docx</button>
              <button className="btn btn-primary" onClick={openNew}><Plus size={14} /> New Template</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
            {filtered.map(t => (
              <div key={t._id} className="card" style={{ padding: '16px', cursor: 'pointer', transition: 'border-color 0.2s' }}
                onClick={() => openEdit(t)}
                onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--zeus-gold)'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--zeus-border)'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)' }}>{t.name}</div>
                    <span className="badge" style={{ fontSize: '0.68rem', marginTop: '4px' }}>{t.category}</span>
                  </div>
                  <FileText size={18} style={{ color: 'var(--zeus-gold)', flexShrink: 0 }} />
                </div>
                {t.description && <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '8px' }}>{t.description}</p>}
                {t.merge_fields?.length > 0 && (
                  <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {t.merge_fields.slice(0, 6).map(f => (
                      <span key={f} style={{ fontSize: '0.65rem', padding: '1px 5px', borderRadius: '3px', background: 'var(--zeus-gold-dim)', color: 'var(--zeus-gold)' }}>{f}</span>
                    ))}
                    {t.merge_fields.length > 6 && <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>+{t.merge_fields.length - 6} more</span>}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{t.updatedAt ? formatDate(t.updatedAt) : '—'}</span>
                  <div style={{ display: 'flex', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleDuplicate(t)} title="Duplicate"><Copy size={14} /></button>
                    <button className="btn btn-ghost btn-sm" onClick={() => openGenerate(t)} title="Generate"><Wand2 size={14} /></button>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(t)} title="Delete" style={{ color: 'var(--status-red)' }}><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ═══ FORMS SECTION ═══ */}
        {!loading && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '28px', marginBottom: '14px' }}>
              <ClipboardList size={18} style={{ color: '#3b82f6' }} />
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                Forms
              </h2>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>({filteredForms.length})</span>
            </div>

            {filteredForms.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', border: '1px dashed var(--zeus-border)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)' }}>
                <ClipboardList size={22} style={{ marginBottom: '6px', opacity: 0.5 }} />
                <div style={{ fontSize: '0.88rem' }}>No forms yet</div>
                <button className="btn btn-primary btn-sm" onClick={() => navigate('/form-builder/new')} style={{ marginTop: '10px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', borderColor: '#3b82f6' }}><Plus size={14} /> Create Form</button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
                {filteredForms.map(f => (
                  <div key={f._id} className="card" style={{ padding: '16px', cursor: 'pointer', transition: 'border-color 0.2s' }}
                    onClick={() => navigate(`/form-builder/${f._id}`)}
                    onMouseOver={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                    onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--zeus-border)'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)' }}>{f.name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', fontWeight: 700,
                            background: f.status === 'published' ? 'rgba(34,197,94,0.12)' : 'rgba(100,116,139,0.12)',
                            color: f.status === 'published' ? '#22c55e' : '#64748b',
                          }}>
                            {f.status === 'published' ? <><Globe size={10} /> Published</> : <><Lock size={10} /> Draft</>}
                          </span>
                          {f.submission_count > 0 && (
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{f.submission_count} submission{f.submission_count !== 1 ? 's' : ''}</span>
                          )}
                        </div>
                      </div>
                      <ClipboardList size={18} style={{ color: '#3b82f6', flexShrink: 0 }} />
                    </div>
                    {f.description && <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '8px' }}>{f.description}</p>}
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '8px' }}>
                      {(f.fields || []).length} field{(f.fields || []).length !== 1 ? 's' : ''}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{f.updatedAt ? formatDate(f.updatedAt) : '—'}</span>
                      <div style={{ display: 'flex', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
                        {f.status === 'published' && f.publish_token && (
                          <button className="btn btn-ghost btn-sm" title="Copy Link"
                            onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/forms/submit/${f.publish_token}`); toast.success('Link copied'); }}>
                            <LinkIcon size={14} />
                          </button>
                        )}
                        {f.status === 'published' && f.publish_token && (
                          <a href={`/forms/submit/${f.publish_token}`} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" title="Preview"><ExternalLink size={14} /></a>
                        )}
                        <button className="btn btn-ghost btn-sm" title="Duplicate" onClick={async () => { try { await duplicateForm(f._id); toast.success('Form duplicated'); load(); } catch { toast.error('Failed'); } }}><Copy size={14} /></button>
                        <button className="btn btn-ghost btn-sm" title="Delete" style={{ color: 'var(--status-red)' }}
                          onClick={() => setConfirmAction({ title:'Delete Form', message:`Delete "${f.name}"? This cannot be undone.`, confirmLabel:'Delete', danger:true, onConfirm: async () => { try { await deleteForm(f._id); toast.success('Form deleted'); load(); } catch { toast.error('Failed'); } } })}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {showGenerate && (
        <Modal title={`Generate: ${showGenerate?.name || ''}`} onClose={() => setShowGenerate(null)}>
          <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>Select a case and/or client to auto-fill the template merge fields.</p>
          <div className="form-group">
            <label className="form-label">Case</label>
            <select className="form-select" value={genForm.case_id} onChange={(e) => setGenForm({ ...genForm, case_id: e.target.value })}>
              <option value="">Select a case...</option>
              {cases.map(c => <option key={c._id || c.id} value={c._id || c.id}>{c.case_number} — {c.client_name || c.claimant?.name || 'Unknown'}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Client (optional, auto-filled from case)</label>
            <select className="form-select" value={genForm.client_id} onChange={(e) => setGenForm({ ...genForm, client_id: e.target.value })}>
              <option value="">Auto from case</option>
              {clients.map(c => <option key={c._id || c.id} value={c._id || c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
            <button className="btn btn-ghost" onClick={() => setShowGenerate(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}><Wand2 size={14} /> {generating ? 'Generating...' : 'Generate Document'}</button>
          </div>
        </Modal>
      )}

      {showGenerated && (
        <Modal title="Generated Documents" onClose={() => setShowGenerated(false)} wide>
          {generatedDocs.length === 0 ? (
            <div className="empty-state" style={{ padding: '32px' }}><FileText /><h3>No generated documents</h3><p>Generate a document from a template to see it here.</p></div>
          ) : (
            <div className="table-container">
              <table>
                <thead><tr><th>Document</th><th>Template</th><th>Category</th><th>Generated</th><th>By</th><th></th></tr></thead>
                <tbody>
                  {generatedDocs.map(doc => (
                    <tr key={doc._id}>
                      <td style={{ color: 'var(--zeus-gold)', fontWeight: 500 }}>{doc.name}</td>
                      <td>{doc.template?.name || '—'}</td>
                      <td><span className="badge badge-medium" style={{ fontSize: '0.68rem' }}>{doc.category}</span></td>
                      <td style={{ fontSize: '0.82rem' }}>{formatDate(doc.createdAt)}</td>
                      <td style={{ fontSize: '0.82rem' }}>{doc.generated_by ? `${doc.generated_by.first_name} ${doc.generated_by.last_name}` : '—'}</td>
                      <td><button className="btn btn-ghost btn-sm" onClick={async () => {
                        const token = localStorage.getItem('zeus_token');
                        const resp = await fetch(`/api/templates/documents/generated/${doc._id}?format=preview`, { headers: { 'Authorization': `Bearer ${token}` } });
                        const html = await resp.text();
                        const win = window.open('', '_blank');
                        if (win) { win.document.open(); win.document.write(html); win.document.close(); }
                      }}><Eye size={14} /> View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}

      {/* ═══ PDF Field Mapping Modal ═══ */}
      {showPdfMapping && selected && (
        <div className="modal-overlay" onClick={() => setShowPdfMapping(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                Map Fields: {selected.name || 'PDF Template'}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowPdfMapping(false)} style={{ padding: '4px 8px' }}>
                <X size={14} />
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
                {pdfFields.length > 0
                  ? `${pdfFields.length} fillable field${pdfFields.length !== 1 ? 's' : ''} detected. Map each to the case data it should auto-fill with.`
                  : 'No fillable form fields detected in this PDF. You can still add field mappings manually below. For non-fillable PDFs, the document will be saved as-is to the case and can be sent via DocuSeal for signatures.'
                }
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pdfMapping.map((m, i) => {
                  const isAutoDetected = pdfFields.some(f => f.name === m.pdf_field);
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                      background: 'rgba(255,255,255,0.03)', border: '1px solid var(--zeus-border)',
                      borderRadius: 'var(--radius-sm)',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {isAutoDetected ? (
                          <>
                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {m.pdf_field}
                            </div>
                            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                              detected field
                            </div>
                          </>
                        ) : (
                          <input
                            className="form-input"
                            placeholder="Field name"
                            value={m.pdf_field}
                            onChange={e => setPdfMapping(prev => prev.map((p, idx) => idx === i ? { ...p, pdf_field: e.target.value } : p))}
                            style={{ padding: '4px 8px', fontSize: '0.82rem' }}
                          />
                        )}
                      </div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', flexShrink: 0 }}>-&gt;</span>
                      <select
                        className="form-select"
                        value={m.merge_key}
                        onChange={e => setPdfMapping(prev => prev.map((p, idx) => idx === i ? { ...p, merge_key: e.target.value } : p))}
                        style={{ width: 200, padding: '5px 8px', fontSize: '0.82rem', flexShrink: 0 }}
                      >
                        <option value="">-- Not mapped --</option>
                        {[
                          { label: 'Contact', group: 'contact' },
                          { label: 'Case', group: 'case' },
                          { label: 'Insurance', group: 'insurance' },
                          { label: 'Employer', group: 'employer' },
                          { label: 'Attorney / Firm', group: 'attorney' },
                          { label: 'System', group: 'system' },
                        ].map(g => {
                          const fields = ALL_MERGE_FIELDS.filter(f => f.group === g.group);
                          if (fields.length === 0) return null;
                          return (
                            <optgroup key={g.group} label={g.label}>
                              {fields.map(f => (
                                <option key={f.key} value={f.key}>{f.label}</option>
                              ))}
                            </optgroup>
                          );
                        })}
                      </select>
                      {!isAutoDetected && (
                        <button className="btn btn-ghost btn-sm" style={{ padding: '4px', color: 'var(--status-red)', flexShrink: 0 }}
                          onClick={() => setPdfMapping(prev => prev.filter((_, idx) => idx !== i))}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <button className="btn btn-ghost btn-sm" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 5 }}
                onClick={() => setPdfMapping(prev => [...prev, { pdf_field: '', merge_key: '' }])}>
                <Plus size={13} /> Add Field
              </button>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowPdfMapping(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSavePdfMapping} disabled={pdfMappingSaving}>
                {pdfMappingSaving ? 'Saving...' : 'Save Mapping'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => { await confirmAction?.onConfirm(); setConfirmAction(null); }}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel || 'Confirm'}
        danger={confirmAction?.danger !== false}
      />

    </>
  );
}
